/**
 * @file The Home table's header row — one `DraggableHeaderCell` per visible
 * column, plus the filter options each cell's menu offers.
 *
 * This is where filter-option order is established. A column supplying
 * `getFilterOptions` keeps the order it declared; a row-derived facet is
 * ordered here by value, because its first-appearance order shifts with every
 * search. `ColumnFilter` then renders what it is handed, re-sorting only the
 * user columns to pin the current user first.
 *
 * In card mode the header is a static, wrapping strip of column chips instead of
 * a sticky grid row; reordering is disabled there and each chip keeps its menu.
 */
import { useContext, useMemo } from 'react';
import { theme } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

import type { BaseListDocument, ColumnSpec } from '../../DocList.types';
import { DocListStoreContext } from '../../createDocListTableStore';
import DraggableHeaderCell from './DraggableHeaderCell';

const EMPTY_FACET_MAP = new Map<string, number>();

interface DocListTableHeaderProps<TDoc extends BaseListDocument> {
  gridTemplateColumns: string;
  visibleColumns: ColumnSpec<TDoc>[];
  data: TDoc[];
  frozenLeftOffsets: Map<string, number>;
  getLabel?: (columnId: string, value: unknown) => string;
  onOpenContextMenu?: (columnId: string, position: { x: number; y: number }) => void;
  enableFacetedValues?: boolean;
  cardMode?: boolean;
}

function DocListTableHeader<TDoc extends BaseListDocument>({
  gridTemplateColumns,
  visibleColumns,
  data,
  frozenLeftOffsets,
  getLabel,
  onOpenContextMenu,
  enableFacetedValues = false,
  cardMode = false,
}: DocListTableHeaderProps<TDoc>) {
  const { token } = theme.useToken();
  const store = useContext(DocListStoreContext);

  const columnIds = visibleColumns.map((col) => col.id);

  const allFacetedValues = useMemo(() => {
    const result = new Map<string, Map<string, number>>();

    for (const col of visibleColumns) {
      if (col.getFilterOptions) {
        result.set(col.id, new Map(col.getFilterOptions().map((o) => [o.value, 0])));
      }
    }

    if (enableFacetedValues) {
      const dataFacetColumns = visibleColumns.filter(
        (col) => col.filterable && col.serverFilterField && !col.getFilterOptions
      );
      for (const col of dataFacetColumns) {
        const counts = new Map<string, number>();
        for (const row of data) {
          const value = col.accessor(row);
          for (const item of Array.isArray(value) ? value : [value]) {
            const key = String(item ?? '');
            counts.set(key, (counts.get(key) ?? 0) + 1);
          }
        }
        result.set(
          col.id,
          new Map([...counts].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
        );
      }
    }

    return result;
  }, [data, visibleColumns, enableFacetedValues]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnIds.indexOf(active.id as string);
      const newIndex = columnIds.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        store.columnOrder = arrayMove(columnIds, oldIndex, newIndex); // eslint-disable-line react-hooks/immutability
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={columnIds}
        strategy={horizontalListSortingStrategy}
      >
        <div
          aria-label="doclist-table-header"
          style={
            cardMode
              ? {
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: token.marginXS,
                  padding: token.paddingXS,
                  position: 'static',
                  background: token.colorBgContainer,
                }
              : {
                  display: 'grid',
                  gridTemplateColumns,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  background: token.colorBgContainer,
                }
          }
        >
          {visibleColumns.map((col) => (
            <DraggableHeaderCell
              key={col.id}
              column={col}
              facetedUniqueValues={allFacetedValues.get(col.id) ?? EMPTY_FACET_MAP}
              getLabel={getLabel}
              frozenLeftOffset={frozenLeftOffsets.get(col.id)}
              onContextMenu={onOpenContextMenu}
              cardMode={cardMode}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default DocListTableHeader;
